import React, {useEffect, useMemo, useRef} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useGetIsOfferSoldOut} from 'hooks/useGetIsOfferSoldOut/useGetIsOfferSoldOut'
import {getCurrencyCode} from 'modules/meta/selectors'
import {
  getAnchorComplete,
  getAnchorHotel,
  getAnchorHotelPreferredRateOffer,
  getAnchorHotelTopOffer,
  getNumberOfRooms,
  getSearchParameters
} from 'modules/sapiSearch/selectors'
import Head from 'next/head'

import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'
import {buildHotelPriceSchema} from '@vio/shared/src/structuredData'

export const HotelPriceStructuredData: React.FC = () => {
  const dispatch = useDispatch()

  const toggle = useToggle()
  const isAnchorComplete = useSelector(getAnchorComplete)
  const anchorHotel = useSelector(getAnchorHotel)
  const preferredRateOffer = useSelector(getAnchorHotelPreferredRateOffer)
  const topOffer = useSelector(getAnchorHotelTopOffer)
  const searchParameters = useSelector(getSearchParameters)
  const currencyCode = useSelector(getCurrencyCode)
  const numberOfRooms =
    useSelector(state => getNumberOfRooms(state, anchorHotel?.objectID)) ?? 1

  const isEnabled = toggle('feature-hotel-price-structured-data', false, true)
  const serveEarly = toggle(
    'feature-hotel-price-structured-data-early-serve',
    false,
    true
  )

  const resolveOffer = () => {
    if (serveEarly && preferredRateOffer) return preferredRateOffer
    if (!isAnchorComplete) return null
    return preferredRateOffer ?? topOffer
  }
  const offer = resolveOffer()

  const isSoldOut = useGetIsOfferSoldOut({offer})

  const schema = useMemo(() => {
    if (
      !isEnabled ||
      !anchorHotel ||
      !currencyCode ||
      !searchParameters.checkIn ||
      !searchParameters.checkOut
    ) {
      return null
    }

    if (!offer || isSoldOut)
      return buildHotelPriceSchema({
        hotel: anchorHotel,
        offer: undefined,
        checkIn: searchParameters.checkIn,
        checkOut: searchParameters.checkOut,
        currencyCode,
        isSoldOut: true,
        numberOfRooms
      })

    return buildHotelPriceSchema({
      hotel: anchorHotel,
      offer,
      checkIn: searchParameters.checkIn,
      checkOut: searchParameters.checkOut,
      currencyCode,
      numberOfRooms
    })
  }, [
    isEnabled,
    anchorHotel,
    offer,
    searchParameters,
    currencyCode,
    isSoldOut,
    numberOfRooms
  ])

  const servedRef = useRef(false)
  useEffect(() => {
    if (!schema || servedRef.current) return
    servedRef.current = true
    dispatch(
      trackEvent({
        category: Category.System,
        entity: Entity.HotelStructuredDataMarkup,
        action: Action.Served,
        payload: {
          page: 'Search',
          offerType: preferredRateOffer ? 'preferred' : 'top',
          serveEarly,
          isSoldOut: isSoldOut || !offer,
          schema
        }
      })
    )
  }, [schema, dispatch, preferredRateOffer, serveEarly, isSoldOut, offer])

  if (!schema) return null

  return (
    <Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(schema)
        }}
      />
    </Head>
  )
}
